import React, { useState } from "react";
import {
  createStyles,
  Box,
  Stack,
  Text,
  Center,
  Image,
  Space,
  Group,
  Anchor,
  Notification,
  Loader,
  Textarea,
  TextInput,
  Button,
} from "@mantine/core";

import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useFormspark } from "@formspark/use-formspark";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { IconCheck, IconX } from "@tabler/icons-react";

const FORMSPARK_FORM_ID = "AoDQ1EjT7";

let schema = yup.object({
  Name: yup.string().min(2).max(20).label("Name").required(),
  Email: yup.string().email().label("Email").required(),
  Message: yup.string().min(2).label("Message").required(),
});

function ContactPage({ children, ...otherProps }) {
  const { classes } = useStyles();
  const navigation = useNavigate();

  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [emailSending, setEmailSending] = useState(false);

  const [submit] = useFormspark({
    formId: FORMSPARK_FORM_ID,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = (data) => {
    if (!data) return;

    const { Name, Email, Message } = data;
    const submitData = { From: Name, Email: Email, Message: Message };

    setEmailSending(true);

    submit(submitData)
      .then((result) => {
        setEmailSuccess(true);
        setEmailError(false);
        setEmailSending(false);
        reset();
      })
      .catch((error) => {
        console.error(error);
        setEmailSuccess(false);
        setEmailError(true);
        setEmailSending(false);
      });
  };

  return (
    <Box className={classes.container} {...otherProps}>
      {/* Header */}
      <Box className={classes.root}>
        <Box className={classes.grid}>
          <Box className={classes.section}>
            <Center px={50} pt={30} pb={10}>
              <Stack spacing={0} justify='center'>
                <Center>
                  <Group spacing={10} align="center" sx={{ cursor: "pointer" }} onClick={() => navigation("/")}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "linear-gradient(135deg, #B049C5, #6A59D8)",
                      }}
                    >
                      <Image
                        src="/flipwave-ico.svg"
                        alt="FlipWave"
                        width={30}
                        height={30}
                        sx={{ objectFit: "contain" }}
                      />
                    </Box>
                    <Text weight={800} size={30} sx={{ color: "#7749FD" }}>
                      FlipWave
                    </Text>
                  </Group>
                </Center>
              </Stack>
            </Center>
          </Box>
          <Space h={30} />
        </Box>
      </Box>

      <Box className={classes.containerTwo}>
        <Box className={classes.root}>
          <Box className={classes.grid}>
            <Box className={classes.section} mt={50} px={30}>
              <Stack spacing={0} justify='left'>
                <Text weight={600} size={26} sx={{ color: "#000000" }}>
                  Contact Us
                </Text>
                <Text weight={400} size={18} sx={{ color: "#87808A" }}>
                  Please provide information below
                </Text>
              </Stack>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Stack align='left' spacing={20}>
                  <TextInput
                    placeholder='Name*'
                    className={classes.input}
                    variant='unstyled'
                    {...register("Name", {
                      required: true,
                      minLength: 2,
                      maxLength: 20,
                    })}
                  />
                  {errors.Name && (
                    <Text weight={700} color='red' pl={15}>
                      {errors.Name?.message}
                    </Text>
                  )}

                  <TextInput
                    placeholder='Email*'
                    className={classes.input}
                    withAsterisk
                    variant='unstyled'
                    {...register("Email")}
                  />
                  {errors.Email && (
                    <Text weight={700} color='red' pl={15}>
                      {errors.Email?.message}
                    </Text>
                  )}

                  <Textarea
                    placeholder='Message*'
                    color='#FAFAFA'
                    variant='unstyled'
                    minRows={6}
                    maxRows={8}
                    className={classes.inputArea}
                    {...register("Message")}
                  />
                  {errors.Message && (
                    <Text weight={700} color='red' pl={15}>
                      {errors.Message?.message}
                    </Text>
                  )}
                  <Stack align='center' mt={30}>
                    {emailSending && (
                      <Stack align='center'>
                        <Loader />
                        <Text>Sending...</Text>
                      </Stack>
                    )}
                    {emailSuccess && (
                      <Notification
                        icon={<IconCheck size={18} />}
                        color='teal'
                        onClose={() => setEmailSuccess(false)}
                      >
                        <Text size='md'>Your message has been sent successfully</Text>
                      </Notification>
                    )}
                    {emailError && (
                      <Notification
                        icon={<IconX size={18} />}
                        color='red'
                        onClose={() => setEmailError(false)}
                      >
                        <Text size='md'>Your message has not been sent</Text>
                      </Notification>
                    )}
                  </Stack>
                  <Center mt={30}>
                    <Button
                      loading={emailSending}
                      type='submit'
                      radius='md'
                      size='lg'
                      sx={{
                        backgroundColor: "#E63230",
                        "&:hover": { backgroundColor: "#d42d2b" },
                      }}
                    >
                      Send a message
                    </Button>
                  </Center>
                </Stack>
              </form>
            </Box>
            <Space h={320} />
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box className={classes.containerFooter}>
        <Box className={classes.root}>
          <Box className={classes.grid} mt={30}>
            <Box className={classes.sectionTwo}>
              <Text weight={400} size={16} align='left' sx={{ color: "#87808A" }}>
                Made with love by Anatoli Niculeac<sup>&copy;</sup> 2026
              </Text>
              <Group spacing={10} p={20} position='right'>
                <Anchor component={Link} to={"/support"} className={classes.link}>
                  <Text weight={500} size={16} align='left' sx={{ color: "#7749FD" }}>
                    Support
                  </Text>
                </Anchor>
                <Text weight={400} size={16} align='left' sx={{ color: "#87808A" }}>
                  |
                </Text>
                <Anchor component={Link} to={"/terms"} className={classes.link}>
                  <Text weight={500} size={16} align='left' sx={{ color: "#7749FD" }}>
                    Terms of use
                  </Text>
                </Anchor>
                <Text weight={400} size={16} align='left' sx={{ color: "#87808A" }}>
                  |
                </Text>
                <Anchor component={Link} to={"/privacy"} className={classes.link}>
                  <Text weight={500} size={16} align='left' sx={{ color: "#7749FD" }}>
                    Privacy Policy
                  </Text>
                </Anchor>
                <Text weight={400} size={16} align='left' sx={{ color: "#87808A" }}>
                  |
                </Text>
                <Anchor component={Link} to={"/contact"} className={classes.link}>
                  <Text weight={500} size={16} align='left' sx={{ color: "#7749FD" }}>
                    Contact
                  </Text>
                </Anchor>
              </Group>
              <Space h={10} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default ContactPage;

const useStyles = createStyles({
  root: {
    height: "100%",
    width: "100%",
    overflowX: "hidden",
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "column",
  },
  container: {
    marginLeft: "auto",
    marginRight: "auto",
    maxWidth: "100%",
    "@media (max-width: 768px)": { width: "100%" },
    width: "100%",
    height: "100%",
    background: "linear-gradient(180deg, #FBE1F6 0%, #FCE9F9 100%)",
  },
  containerTwo: {
    marginLeft: "auto",
    marginRight: "auto",
    maxWidth: "100%",
    "@media (max-width: 768px)": { width: "100%" },
    width: "100%",
    height: "100%",
    backgroundColor: "#FFFFFF",
  },
  containerFooter: {
    marginLeft: "auto",
    marginRight: "auto",
    maxWidth: "100%",
    "@media (max-width: 768px)": { width: "100%" },
    width: "100%",
    height: "100%",
    background: "linear-gradient(180deg, #FCE9F9 0%, #FBE1F6 100%)",
  },
  section: {
    maxWidth: 980,
    minWidth: 350,
    width: "70%",
    marginLeft: "auto",
    marginRight: "auto",
    display: "grid",
    gridTemplateColumns: "1fr",
    gridTemplateRows: "1fr",
    gridGap: "1em",
    gap: 25,
    alignItems: "center",
    paddingLeft: 25,
    paddingRight: 25,
  },
  input: {
    backgroundColor: "#FAFAFA",
    paddingLeft: 15,
    paddingRight: 15,
    paddingTop: 15,
    borderRadius: 10,
    border: "1px solid #F0F0F0",
    color: "#F6F6F6",
    fontSize: 14,
    minHeight: 55,
  },
  inputArea: {
    backgroundColor: "#FAFAFA",
    padding: 15,
    borderRadius: 10,
    border: "1px solid #F0F0F0",
    color: "#F6F6F6",
    height: 190,
    fontSize: 14,
  },
  sectionTwo: {
    maxWidth: 980,
    marginLeft: "auto",
    marginRight: "auto",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridTemplateRows: "1fr",
    gridGap: "1em",
    gap: "1em",
    alignItems: "center",
    paddingLeft: 25,
    paddingRight: 25,
  },
  grid: {
    display: "grid",
    width: "100%",
    height: "100%",
    gridTemplateColumns: "auto",
    gridTemplateRows: "auto",
    "@media (max-width: 1023px)": {
      gridTemplateColumns: "auto",
      justifyContent: "stretch",
    },
    "@media (min-width: 1600px)": {
      gridTemplateColumns: "auto",
      gridTemplateRows: "auto",
      WebkitTransition: "300ms",
    },
  },
  link: {
    whiteSpace: "nowrap",
    textDecoration: "none",
    ":last-child": { borderRight: "none" },
    transition: "opacity 0.3s cubic-bezier(0.25, 0.1, 0.25, 1)",
    ":hover": {
      textDecoration: "none",
      opacity: 0.7,
    },
  },
});
